import { ROBOT_MASS_TARGET } from './motorisation'
/**
 * Die Massenbilanz — was am Balancieren an der Masse hängt und was nicht.
 *
 * DIE FRAGE, DIE DIESE SEITE BEANTWORTET: ein Roboter soll leichter werden.
 * Was wird dadurch besser? Die übliche Antwort lautet „alles", und sie ist
 * falsch. Die Grunddynamik des Balancierens enthält überhaupt keine Masse:
 * Zeitkonstante, Capture Point, Störungswachstum über die Regellatenz und der
 * auffangbare IMPULS sind bei 40 kg dieselben wie bei 80.
 *
 * Massenabhängig ist alles, was über das MOMENT läuft. Bei festem Höchstmoment
 * halbieren sich der haltbare Neigungswinkel und die auffangbare
 * Schwerpunktgeschwindigkeit, wenn die Masse verdoppelt wird.
 *
 * Und dann gibt es die Rückkopplung: jedes zusätzliche Newtonmeter kostet
 * Aktuatormasse, und die verlangt wieder Moment. `1/(1 − f)` sagt, wie stark
 * sich diese Schleife aufschaukelt. Nähert sich f der Eins, divergiert der
 * Entwurf — mit der gewählten Momentdichte ist dann kein Roboter mehr baubar.
 *
 * WARUM DAS EIN EIGENES MODUL IST UND NICHT TEIL DER GANGRECHNUNG:
 * es rechnet nicht die Maschine, sondern die ENTWURFSFRAGE über der Masse. Es
 * teilt keinen Zustand mit dem Gang, hängt an keinem Gelenkpunkt, und die
 * Auslegungsgrössen (Momentdichte, Akkukapazität, Cost of Transport) sind
 * Annahmen über einen Bauzustand, nicht Masse des Gen5.
 */

/** Normfallbeschleunigung. Hier die genaue, nicht 9,81 — die Werte sind klein. */
const G = 9.80665

/**
 * Kupferverlust je Nm² Haltemoment, W/Nm².
 *
 * Halten kostet Strom ohne Arbeit zu leisten: die Verlustleistung geht mit dem
 * QUADRAT des Moments. Deshalb ist die Halteleistung im Stand die einzige
 * Grösse dieser Seite, die mit m² wächst — und deshalb ist ein Humanoid mit
 * dauerhaft gebeugten Knien energetisch so teuer.
 */
const COPPER_HOLD = 0.0012

/**
 * ISO/TS 15066: Brustkorb.
 *
 * `CHEST_MASS` ist die effektive Körpermasse des Menschen (kg), `CHEST_ENERGY`
 * die Energiegrenze (J). Beide sind aus den in der Norm veröffentlichten
 * Beispielwerten ZURÜCKGERECHNET — die Norm nennt Kraft- und Druckgrenzen, aus
 * denen sich diese Energie ergibt. Wer damit eine Abnahme begründen will,
 * braucht die Norm selbst, nicht diese Zahlen.
 */
const CHEST_MASS = 40
const CHEST_ENERGY = 1.08

/* -------------------------------------------------------------------------
   Die Eingaben
   ------------------------------------------------------------------------- */

export interface BalanceParams {
  /** Vergleichsmasse A, kg. */
  massA: number
  /** Vergleichsmasse B, kg. */
  massB: number
  /** Schwerpunkthöhe über dem Boden, m. */
  comHeight: number
  /** Höchstmoment der massgebenden Achse, Nm. */
  peakMoment: number
  /** Wirkdauer einer Korrektur, s. */
  actionTime: number
  /** Regelverzug bis zum Eingriff, s. */
  latency: number
  /** Fusslänge vor der Sprunggelenkachse, m. */
  footAhead: number
  /** Reibbeiwert der Sohle. */
  friction: number
  /** Marschgeschwindigkeit, m/s. */
  speed: number
  /** Aufsetzgeschwindigkeit des Fusses, m/s. */
  touchdown: number
  /** Nutzlast, kg. */
  payload: number
  /** Momentdichte des Antriebsstrangs, Nm/kg. */
  torqueDensity: number
  /** Zahl der momentbestimmenden Gelenke. */
  joints: number
  /** Cost of Transport — Energie je Meter und Newton Gewicht. */
  costOfTransport: number
  /** Rückgewinnungsgrad über den ganzen Weg (Round Trip). */
  recovery: number
  /** Akkukapazität, kWh. */
  battery: number
  /** Grundlast der Elektronik, W. */
  idlePower: number
  /** Gefälle, %. */
  grade: number
}

/*
  DIE BEIDEN MASSEN SIND KEINE RUNDEN ZAHLEN MEHR, sondern die zwei, um die es
  wirklich geht: die ZIELMASSE der Maschine und die Masse, bei der der Entwurf
  vorher lag. Vorher standen hier 40 und 80 kg — ein Lehrbeispiel für den Faktor
  zwei, das mit diesem Roboter nichts zu tun hatte.

  Damit beantwortet die Seite die Frage, die gerade offen ist: was gewinnt man,
  wenn man die Zielmasse hält, und was verliert man, wenn nicht.
*/
export const DEFAULT_BALANCE: BalanceParams = {
  massA: ROBOT_MASS_TARGET,
  massB: 80,
  comHeight: 0.9,
  peakMoment: 150,
  actionTime: 0.15,
  latency: 0.04,
  footAhead: 0.15,
  friction: 0.7,
  speed: 1,
  touchdown: 0.35,
  payload: 0,
  torqueDensity: 20,
  joints: 6,
  costOfTransport: 3.2,
  recovery: 0.72,
  battery: 1.5,
  idlePower: 60,
  grade: 10,
}

export interface BalanceField {
  key: keyof BalanceParams
  label: string
  min: number
  max: number
  step: number
  unit: string
  /** Nachkommastellen in der Anzeige. */
  digits: number
}

export const BALANCE_FIELDS: BalanceField[] = [
  { key: 'massA', label: 'Masse A', min: 10, max: 140, step: 1, unit: 'kg', digits: 0 },
  { key: 'massB', label: 'Masse B', min: 10, max: 140, step: 1, unit: 'kg', digits: 0 },
  {
    key: 'comHeight',
    label: 'Schwerpunkthöhe zc',
    min: 0.4,
    max: 1.3,
    step: 0.01,
    unit: 'm',
    digits: 2,
  },
  {
    key: 'peakMoment',
    label: 'Gelenkmoment Mmax',
    min: 30,
    max: 600,
    step: 5,
    unit: 'Nm',
    digits: 0,
  },
  {
    key: 'actionTime',
    label: 'Wirkdauer Δt',
    min: 0.05,
    max: 0.5,
    step: 0.01,
    unit: 's',
    digits: 2,
  },
  { key: 'latency', label: 'Regelverzug τ', min: 0, max: 0.2, step: 0.005, unit: 's', digits: 3 },
  {
    key: 'footAhead',
    label: 'Fusslänge vor der Achse',
    min: 0.05,
    max: 0.3,
    step: 0.005,
    unit: 'm',
    digits: 3,
  },
  { key: 'friction', label: 'Reibbeiwert µ', min: 0.2, max: 1.2, step: 0.02, unit: '', digits: 2 },
  { key: 'speed', label: 'Geschwindigkeit', min: 0.2, max: 10, step: 0.05, unit: 'm/s', digits: 2 },
  {
    key: 'touchdown',
    label: 'Aufsetzgeschwindigkeit',
    min: 0.05,
    max: 1.5,
    step: 0.05,
    unit: 'm/s',
    digits: 2,
  },
  { key: 'payload', label: 'Nutzlast', min: 0, max: 60, step: 1, unit: 'kg', digits: 0 },
  {
    key: 'torqueDensity',
    label: 'Momentdichte des Antriebs',
    min: 5,
    max: 45,
    step: 1,
    unit: 'Nm/kg',
    digits: 0,
  },
  {
    key: 'joints',
    label: 'momentbestimmende Gelenke',
    min: 2,
    max: 14,
    step: 1,
    unit: '',
    digits: 0,
  },
  {
    key: 'costOfTransport',
    label: 'Cost of Transport',
    min: 0.1,
    max: 6,
    step: 0.1,
    unit: '',
    digits: 1,
  },
  {
    key: 'recovery',
    label: 'Rückgewinnungsgrad η',
    min: 0,
    max: 0.95,
    step: 0.01,
    unit: '',
    digits: 2,
  },
  { key: 'battery', label: 'Akkukapazität', min: 0.3, max: 4, step: 0.1, unit: 'kWh', digits: 1 },
  {
    key: 'idlePower',
    label: 'Grundlast Elektronik',
    min: 0,
    max: 400,
    step: 5,
    unit: 'W',
    digits: 0,
  },
  { key: 'grade', label: 'Gefälle', min: 0, max: 30, step: 1, unit: '%', digits: 0 },
]

/* -------------------------------------------------------------------------
   Die Rechnung
   ------------------------------------------------------------------------- */

export interface BalanceValues {
  /** Gesamtmasse inklusive Nutzlast, kg. */
  total: number
  /** Zeitkonstante des inversen Pendels, s. */
  timeConstant: number
  /** Nutzbare Wirkdauer nach Abzug des Regelverzugs, s. */
  effectiveTime: number
  /** Faktor, um den eine Störung über den Regelverzug wächst. */
  growth: number
  /** Haltbarer Neigungswinkel, Grad. */
  tiltAngle: number
  /** Verschiebung des Druckmittelpunkts aus dem Höchstmoment, m. */
  copShift: number
  /** Davon nutzbar — begrenzt durch die Fusslänge, m. */
  copUsable: number
  /** Auffangbare Schwerpunktgeschwindigkeit, m/s. */
  catchSpeed: number
  /** Auffangbarer Impuls, Ns. */
  catchImpulse: number
  /** Capture-Point-Reichweite, m. */
  capturePoint: number
  /** Sturzenergie, J. */
  fallEnergy: number
  /** Kinetische Energie bei Marschgeschwindigkeit, J. */
  kineticEnergy: number
  /** Aufsetzimpuls, Ns. */
  touchdownImpulse: number
  /** Reibgrenze der Beschleunigung, m/s². */
  frictionLimit: number
  /** Froude-Zahl. */
  froude: number
  /** Zulässige Kontaktgeschwindigkeit nach ISO/TS 15066, m/s. */
  contactSpeed: number
  /** Energie je Meter Weg, J/m. */
  energyPerMetre: number
  /** Momentbedarf je kg Körpermasse, Nm/kg. */
  momentPerMass: number
  /** Aktuatormasse der Hauptachsen, kg. */
  actuatorMass: number
  /** Ihr Anteil an der Gesamtmasse. */
  actuatorShare: number
  /** Rückkopplungsfaktor der Massenschleife. */
  loopFactor: number
  /** Massenverstärkung 1/(1−f); unendlich, wenn der Entwurf divergiert. */
  amplification: number
  /** Rückgewinnbare Energie beim Anhalten, J. */
  stopEnergy: number
  /** Rückgewinnung je Meter im Gefälle, J/m. */
  descentEnergy: number
  /** Ihr Anteil an der Fahrenergie. */
  recoveryShare: number
  /** Fahrleistung, W. */
  drivePower: number
  /** Halteleistung im Stand, W. */
  holdPower: number
  /** Reichweite, m. */
  range: number
  /** Standzeit, h. */
  standTime: number
  /** Zahl der Bremsungen aus einer Akkuladung. */
  stopCount: number
  /** Kürzeste Bremszeit, s. */
  stopTime: number
  /** Bremsleistung dabei, W. */
  brakePower: number
  /** Davon rückspeisbar, W. */
  brakeRecovered: number
}

/**
 * Rechnet alle Grössen für eine Masse.
 *
 * `mass` ist die LEERMASSE; die Nutzlast kommt aus den Parametern und wird
 * addiert. Das ist die Trennung, die die Seite überhaupt erst nützlich macht:
 * eine Nutzlast von 20 kg wirkt in den Momentgleichungen genau wie 20 kg
 * Eigenmasse, aber sie ist eine Anforderung und keine Entwurfsgrösse.
 */
export function balanceValues(params: BalanceParams, mass: number): BalanceValues {
  const p = params
  const total = mass + p.payload
  const weight = total * G

  const timeConstant = Math.sqrt(p.comHeight / G)
  /* Bleibt immer etwas Wirkdauer übrig: ein Regelverzug grösser als die
     Wirkdauer heisst nicht „negativ reagieren", sondern „zu spät". */
  const effectiveTime = Math.max(0.005, p.actionTime - p.latency)
  const growth = Math.cosh(p.latency / timeConstant)

  const copShift = p.peakMoment / weight
  const copUsable = Math.min(copShift, p.footAhead)
  const tiltAngle = (Math.asin(Math.min(1, p.peakMoment / (weight * p.comHeight))) * 180) / Math.PI
  const catchSpeed = (p.peakMoment * effectiveTime) / (total * p.comHeight)
  const catchImpulse = (p.peakMoment * effectiveTime) / p.comHeight
  const capturePoint = catchSpeed * timeConstant

  const fallEnergy = weight * p.comHeight
  const kineticEnergy = 0.5 * total * p.speed * p.speed
  const touchdownImpulse = total * p.touchdown
  const frictionLimit = p.friction * G
  const froude = (p.speed * p.speed) / (G * p.comHeight)

  /* ISO/TS 15066: bewegte Ersatzmasse ist Nutzlast plus halbe Gesamtmasse. */
  const robotMass = p.payload + total / 2
  const reduced = 1 / (1 / CHEST_MASS + 1 / robotMass)
  const contactSpeed = Math.sqrt((2 * CHEST_ENERGY) / reduced)

  const energyPerMetre = p.costOfTransport * weight
  const momentPerMass = G * copUsable
  const actuatorMass = (p.joints * p.peakMoment) / p.torqueDensity
  const actuatorShare = actuatorMass / total
  const loopFactor = (p.joints * momentPerMass) / p.torqueDensity
  const amplification = loopFactor < 0.98 ? 1 / (1 - loopFactor) : Infinity

  const stopEnergy = kineticEnergy * p.recovery
  const descentEnergy = weight * (p.grade / 100) * p.recovery
  const recoveryShare = energyPerMetre > 0 ? descentEnergy / energyPerMetre : 0

  const drivePower = energyPerMetre * p.speed
  const holdPower = p.idlePower + COPPER_HOLD * p.joints * Math.pow(weight * copUsable, 2)
  const batteryJoules = p.battery * 3.6e6
  /* Die Grundlast geht über die Fahrzeit ein, nicht über den Weg — deshalb
     geteilt durch die Geschwindigkeit. Bei Schrittgeschwindigkeit ist sie der
     grössere Posten, und genau das ist die unangenehme Erkenntnis. */
  const range = batteryJoules / (energyPerMetre + p.idlePower / Math.max(0.05, p.speed))
  const standTime = holdPower > 0 ? batteryJoules / holdPower / 3600 : 0
  const stopCount = stopEnergy > 0 ? batteryJoules / stopEnergy : 0

  const stopTime = p.speed / frictionLimit
  const brakePower = stopTime > 0 ? kineticEnergy / stopTime : 0
  const brakeRecovered = brakePower * p.recovery

  return {
    total,
    timeConstant,
    effectiveTime,
    growth,
    tiltAngle,
    copShift,
    copUsable,
    catchSpeed,
    catchImpulse,
    capturePoint,
    fallEnergy,
    kineticEnergy,
    touchdownImpulse,
    frictionLimit,
    froude,
    contactSpeed,
    energyPerMetre,
    momentPerMass,
    actuatorMass,
    actuatorShare,
    loopFactor,
    amplification,
    stopEnergy,
    descentEnergy,
    recoveryShare,
    drivePower,
    holdPower,
    range,
    standTime,
    stopCount,
    stopTime,
    brakePower,
    brakeRecovered,
  }
}

/* -------------------------------------------------------------------------
   Die Tabelle
   ------------------------------------------------------------------------- */

/**
 * Wie eine Grösse mit der Masse geht.
 *
 * Diese Spalte ist der Kern der Seite. Sie steht als DATUM und nicht als
 * abgeleitete Rechnung, weil sie eine Aussage über die Formel ist und nicht
 * über die Zahl — man liest sie ab, bevor man den Schieberegler anfasst.
 */
export type MassScaling =
  | 'frei'
  | '1/m'
  | '∝m'
  | '1/√µred'
  | 'begrenzt'
  | 'konstant'
  /*
    DIE DREI ZUSAMMENGESETZTEN. Die Vorlage schreibt hier „∝ m²" und „1/m²" —
    die Kurzschrift für den führenden Term. Sie ist irreführend: die Grundlast
    der Elektronik ist ein konstanter Summand, und bei 40 kg macht sie mehr als
    zwei Drittel der Halteleistung aus. Wer nach „∝ m²" die halbe Masse für die
    vierfache Standzeit hält, wird enttäuscht — es ist der Faktor zwei.
  */
  /*
    UND DIE VIERTE. Die Vorlage schreibt „1/√m". Auch das ist die Asymptote:
    µred = 1/(1/mH + 1/mR) ist eine REDUZIERTE Masse und sättigt gegen die
    effektive Körpermasse des Menschen. Ein doppelt so schwerer Roboter trifft
    nicht doppelt so hart — ab etwa der doppelten Menschmasse ändert sich fast
    nichts mehr, weil dann der Mensch nachgibt und nicht der Roboter.
  */
  | 'P₀ + ∝m²'
  | '1/(P₀ + ∝m²)'
  | '1/(∝m + P₀/v)'

export interface BalanceRow {
  label: string
  /** Die Formel in lesbarer Schreibweise. */
  formula: string
  /** Der Wert, bereits in der Anzeigeeinheit. */
  value: (v: BalanceValues) => number
  unit: string
  digits: number
  scaling: MassScaling
  /** Prefix vor der Zahl, etwa `×` beim Störungswachstum. */
  prefix?: string
}

export interface BalanceGroup {
  title: string
  rows: BalanceRow[]
}

export const BALANCE_GROUPS: BalanceGroup[] = [
  {
    title: 'Balance',
    rows: [
      {
        label: 'Zeitkonstante',
        formula: 'Tc = √(zc/g)',
        value: (v) => v.timeConstant,
        unit: 's',
        digits: 3,
        scaling: 'frei',
      },
      {
        label: 'Störungswachstum über τ',
        formula: 'cosh(τ/Tc)',
        value: (v) => v.growth,
        unit: '',
        digits: 2,
        scaling: 'frei',
        prefix: '×',
      },
      {
        label: 'Nutzbare Wirkdauer',
        formula: 'Δteff = Δt − τ',
        value: (v) => v.effectiveTime,
        unit: 's',
        digits: 3,
        scaling: 'frei',
      },
      {
        label: 'Froude-Zahl',
        formula: 'Fr = v²/(g·zc)',
        value: (v) => v.froude,
        unit: '',
        digits: 3,
        scaling: 'frei',
      },
      {
        label: 'Auffangbarer Impuls',
        formula: 'm·vmax = Mmax·Δteff/zc',
        value: (v) => v.catchImpulse,
        unit: 'Ns',
        digits: 1,
        scaling: 'frei',
      },
      {
        label: 'Reibgrenze',
        formula: 'amax = µ·g',
        value: (v) => v.frictionLimit,
        unit: 'm/s²',
        digits: 2,
        scaling: 'frei',
      },
    ],
  },
  {
    title: 'Moment und Reserve',
    rows: [
      {
        label: 'Haltbarer Neigungswinkel',
        formula: 'θmax = Mmax/(m·g·zc)',
        value: (v) => v.tiltAngle,
        unit: '°',
        digits: 1,
        scaling: '1/m',
      },
      {
        label: 'COP-Verschiebung aus dem Moment',
        formula: 'd = Mmax/(m·g)',
        value: (v) => v.copShift * 100,
        unit: 'cm',
        digits: 1,
        scaling: '1/m',
      },
      {
        label: '… davon nutzbar',
        formula: 'deff = min(d, lf)',
        value: (v) => v.copUsable * 100,
        unit: 'cm',
        digits: 1,
        scaling: 'begrenzt',
      },
      {
        label: 'Auffangbare Geschwindigkeit',
        formula: 'vmax = Mmax·Δteff/(m·zc)',
        value: (v) => v.catchSpeed,
        unit: 'm/s',
        digits: 2,
        scaling: '1/m',
      },
      {
        label: 'Capture-Point-Reichweite',
        formula: 'ξ = vmax·Tc',
        value: (v) => v.capturePoint * 100,
        unit: 'cm',
        digits: 1,
        scaling: '1/m',
      },
    ],
  },
  {
    title: 'Folgen',
    rows: [
      {
        label: 'Kinetische Energie',
        formula: 'E = ½·m·v²',
        value: (v) => v.kineticEnergy,
        unit: 'J',
        digits: 0,
        scaling: '∝m',
      },
      {
        label: 'Aufsetzimpuls',
        formula: 'J = m·Δv',
        value: (v) => v.touchdownImpulse,
        unit: 'Ns',
        digits: 1,
        scaling: '∝m',
      },
      {
        label: 'Sturzenergie',
        formula: 'E = m·g·zc',
        value: (v) => v.fallEnergy,
        unit: 'J',
        digits: 0,
        scaling: '∝m',
      },
      {
        label: 'Energie pro Meter',
        formula: 'E/s = CoT·m·g',
        value: (v) => v.energyPerMetre,
        unit: 'J/m',
        digits: 0,
        scaling: '∝m',
      },
      {
        label: 'Zulässige Kontaktgeschwindigkeit',
        formula: 'v = √(2Emax/µred)',
        value: (v) => v.contactSpeed * 100,
        unit: 'cm/s',
        digits: 0,
        scaling: '1/√µred',
      },
    ],
  },
  {
    title: 'Energie, Rückgewinnung und Reichweite',
    rows: [
      {
        label: 'Fahrleistung',
        formula: 'P = CoT·m·g·v',
        value: (v) => v.drivePower,
        unit: 'W',
        digits: 0,
        scaling: '∝m',
      },
      {
        label: 'Halteleistung im Stand',
        formula: 'Phalt = P₀ + c·n·(m·g·deff)²',
        value: (v) => v.holdPower,
        unit: 'W',
        digits: 0,
        scaling: 'P₀ + ∝m²',
      },
      {
        label: 'Anhalten aus der Fahrt',
        formula: 'E = ½·m·v²·η',
        value: (v) => v.stopEnergy,
        unit: 'J',
        digits: 0,
        scaling: '∝m',
      },
      {
        label: 'Rückgewinnung im Gefälle',
        formula: 'E/s = m·g·s·η',
        value: (v) => v.descentEnergy,
        unit: 'J/m',
        digits: 0,
        scaling: '∝m',
      },
      {
        label: '… Anteil an der Fahrenergie',
        formula: 'm·g·s·η / (CoT·m·g)',
        value: (v) => v.recoveryShare * 100,
        unit: '%',
        digits: 0,
        scaling: 'frei',
      },
      {
        label: 'Reichweite',
        formula: 's = EAkku / (E/s + P₀/v)',
        value: (v) => v.range / 1000,
        unit: 'km',
        digits: 2,
        scaling: '1/(∝m + P₀/v)',
      },
      {
        label: 'Standzeit',
        formula: 't = EAkku / Phalt',
        value: (v) => v.standTime,
        unit: 'h',
        digits: 1,
        scaling: '1/(P₀ + ∝m²)',
      },
      {
        label: 'Kürzeste Bremszeit',
        formula: 't = v / (µ·g)',
        value: (v) => v.stopTime,
        unit: 's',
        digits: 2,
        scaling: 'frei',
      },
      {
        label: 'Bremsleistung dabei',
        formula: 'P = ½·m·v·µ·g',
        value: (v) => v.brakePower,
        unit: 'W',
        digits: 0,
        scaling: '∝m',
      },
      {
        label: '… davon rückspeisbar',
        formula: 'P·η',
        value: (v) => v.brakeRecovered,
        unit: 'W',
        digits: 0,
        scaling: '∝m',
      },
    ],
  },
  {
    title: 'Antriebsauslegung',
    rows: [
      {
        label: 'Momentbedarf je kg',
        formula: 'M/m = g·deff',
        value: (v) => v.momentPerMass,
        unit: 'Nm/kg',
        digits: 2,
        scaling: 'frei',
      },
      {
        label: 'Aktuatormasse der Hauptachsen',
        formula: 'mA = n·Mmax/ϱ',
        value: (v) => v.actuatorMass,
        unit: 'kg',
        digits: 1,
        scaling: 'konstant',
      },
      {
        label: '… Anteil an der Gesamtmasse',
        formula: 'mA/m',
        value: (v) => v.actuatorShare * 100,
        unit: '%',
        digits: 0,
        scaling: '1/m',
      },
      {
        label: 'Rückkopplungsfaktor',
        formula: 'f = n·g·deff/ϱ',
        value: (v) => v.loopFactor,
        unit: '',
        digits: 3,
        scaling: 'frei',
      },
      {
        label: 'Massenverstärkung',
        formula: '1/(1 − f)',
        value: (v) => v.amplification,
        unit: '',
        digits: 2,
        scaling: 'frei',
        prefix: '×',
      },
    ],
  },
]

/* -------------------------------------------------------------------------
   Die Kurven über der Masse
   ------------------------------------------------------------------------- */

export interface BalanceCurve {
  key: string
  label: string
  unit: string
  value: (v: BalanceValues) => number
  digits: number
}

export const BALANCE_CURVES: BalanceCurve[] = [
  {
    key: 'tilt',
    label: 'Haltbarer Neigungswinkel',
    unit: '°',
    value: (v) => v.tiltAngle,
    digits: 0,
  },
  {
    key: 'catch',
    label: 'Auffangbare Geschwindigkeit',
    unit: 'm/s',
    value: (v) => v.catchSpeed,
    digits: 2,
  },
  {
    key: 'capture',
    label: 'Capture-Point-Reichweite',
    unit: 'cm',
    value: (v) => v.capturePoint * 100,
    digits: 0,
  },
  {
    key: 'contact',
    label: 'Zulässige Kontaktgeschwindigkeit',
    unit: 'm/s',
    value: (v) => v.contactSpeed,
    digits: 2,
  },
  { key: 'fall', label: 'Sturzenergie', unit: 'J', value: (v) => v.fallEnergy, digits: 0 },
  {
    key: 'perMetre',
    label: 'Energie pro Meter',
    unit: 'J/m',
    value: (v) => v.energyPerMetre,
    digits: 0,
  },
  {
    key: 'share',
    label: 'Aktuatoranteil an der Masse',
    unit: '%',
    value: (v) => v.actuatorShare * 100,
    digits: 0,
  },
  {
    key: 'impulse',
    label: 'Auffangbarer Impuls',
    unit: 'Ns',
    value: (v) => v.catchImpulse,
    digits: 1,
  },
  { key: 'range', label: 'Reichweite', unit: 'km', value: (v) => v.range / 1000, digits: 2 },
  { key: 'stand', label: 'Standzeit', unit: 'h', value: (v) => v.standTime, digits: 1 },
  {
    key: 'stop',
    label: 'Anhalten rückgewinnbar',
    unit: 'J',
    value: (v) => v.stopEnergy,
    digits: 0,
  },
  { key: 'hold', label: 'Halteleistung im Stand', unit: 'W', value: (v) => v.holdPower, digits: 0 },
  {
    key: 'brake',
    label: 'Bremsleistung beim Anhalten',
    unit: 'W',
    value: (v) => v.brakePower,
    digits: 0,
  },
]

/** Der Massenbereich der Kurven, kg — derselbe wie der Bereich der Schieberegler. */
export const BALANCE_MASS_RANGE = { min: 10, max: 140, samples: 66 } as const

/** Die Kurve einer Grösse über der Masse. */
export function balanceCurve(params: BalanceParams, curve: BalanceCurve): Float64Array {
  const { min, max, samples } = BALANCE_MASS_RANGE
  const out = new Float64Array(samples)
  for (let i = 0; i < samples; i++) {
    const mass = min + ((max - min) * i) / (samples - 1)
    out[i] = curve.value(balanceValues(params, mass))
  }
  return out
}

/**
 * Ist eine Grösse über den ganzen Massenbereich konstant?
 *
 * Die Seite hinterlegt die massenfreien Kurven grau. Das wird GEMESSEN und
 * nicht aus der Skalierungsspalte abgelesen: eine Grösse, die laut Formel
 * massenfrei ist, es aber im gewählten Parameterfeld nicht bleibt, soll
 * auffallen statt versteckt zu werden.
 */
export function isMassFree(values: Float64Array): boolean {
  let min = Infinity
  let max = -Infinity
  for (const v of values) {
    if (!Number.isFinite(v)) continue
    if (v < min) min = v
    if (v > max) max = v
  }
  if (!Number.isFinite(min)) return false
  const span = Math.abs(max)
  return span < 1e-12 || (max - min) / span < 1e-6
}
